import React, { useEffect, useState } from 'react';
import AppHeader from '../../component/AppHeader/app-header';
import { useNavigate } from 'react-router-dom';
import './PaymentSuccess.scss';
import { axiosInstanceV2 } from '../../services/axios';
import { useSelector } from 'react-redux';
import Button from '../../component/ui/Button';
import PageLoader from '../../component/PageLoader';

const PaymentSuccessPage = () => {
	const navigate = useNavigate();
	const [loading, setLoading] = useState(true);
	const [data, setData] = useState(null);
	const user = useSelector((state) => state?.userInfo);
	const isOnboardingCompleted = user?.isOnboardingCompleted;
	const subDomain = user?.userInfo?.data?.sub_domain;

	useEffect(() => {
		const fetchPayments = async () => {
			try {
				const authToken = localStorage.getItem('authToken');
				const response = await axiosInstanceV2.get('/subscription/payment-success', {
					headers: { Authorization: `Bearer ${authToken}` },
				});
				setData(response.data.data);
			} catch (err) {
				console.error(err);
			} finally {
				setLoading(false);
			}
		};
		fetchPayments();
	}, []);

	if (loading) {
		return <PageLoader />
	}

	if (!data) {
		return (
			<div className="payment-success-container">
				<div className="payment-card">No payment data found.</div>
			</div>
		);
	}

	// destructure response
	const {
		plan_name,
		plan_key,
		billing_period,
		amount,
		currency,
		subscription_id,
		status,
		is_recurring,
		total_count,
		paid_count,
		current_start,
		current_end,
		next_billing_date,
		payment_id,
		order_id,
		invoice_id,
		invoice_pdf_url,
		payment_details,
		invoice_details,
		sub_total,
		tax_amount,
		total,
		tax_percentage,

	} = data;

	// format helpers
	const formatDate = (date) =>
		date
			? new Date(date).toLocaleDateString('en-IN', {
				day: '2-digit',
				month: 'short',
				year: 'numeric',
			})
			: '-';
	const formatMoney = (amt) => `${currency ?? 'INR'} ₹${(amt ?? 0).toFixed(2)}`;

	// payment method display
	const paymentMethodLabel = () => {
		if (payment_details?.method === 'upi') return `UPI (${payment_details.upi?.vpa ?? '-'})`;
		if (payment_details?.method === 'card')
			return `${payment_details.card?.network} ****${payment_details.card?.last4}`;
		return payment_details?.method ?? '-';
	};

	return (
		<>
			<AppHeader title="Payment Status" />
			<main className="main-wrapper">
				<div className="payment-success-container">
					<div className="payment-card">
						{/* ── HEADER ────────────────────────────────────────────── */}
						<div className="payment-header">
							<div className="success-icon">✔</div>
							<div>
								<p className="order-id">ORDER #{order_id ?? subscription_id ?? '-'}</p>
								<h3>Payment Successful</h3>
								<p className="email-text">Confirmation sent to {payment_details?.email ?? '-'}</p>
							</div>
						</div>

						{/* ── BODY ──────────────────────────────────────────────── */}
						<div className="payment-body">
							{/* LEFT — Plan Details + Next Steps */}
							<div className="plan-details">
								<div className="plan-card">
									<div className="plan-header">
										<h5>Plan Details</h5>
										<span className={`badge badge-${status}`}>{status}</span>
									</div>

									<div className="plan-content">
										<div>
											<strong>{plan_name ?? plan_key ?? 'Plan'}</strong>
											<p>
												{billing_period === 'monthly' ? 'Monthly' : 'Yearly'} Plan
												{is_recurring ? ' • Auto-renewal ON' : ' • One-time'}
											</p>
											<p className="valid">
												Valid: {formatDate(current_start)} – {formatDate(current_end)}
											</p>
											{is_recurring && next_billing_date && (
												<p className="next-billing">
													Next billing: {formatDate(next_billing_date)}
												</p>
											)}
											<p className="cycles">
												Cycles: {paid_count} / {total_count}
											</p>
										</div>
										<span className="price">{formatMoney(amount)}</span>
									</div>
								</div>

								{/* Payment Method */}
								<div className="payment-method-box mt-3">
									<h6>Payment Method</h6>
									<p>{paymentMethodLabel()}</p>
									{payment_details?.captured_at && (
										<p
											className="text-muted"
											style={{ fontSize: 12 }}
										>
											Paid on {formatDate(payment_details.captured_at)}
										</p>
									)}
								</div>

								{/* Next Steps */}
								<h6 className="mt-4">What's Next?</h6>
								{!isOnboardingCompleted && (
									<div className="steps">
										<div className="step-box">
											<span>1</span>
											<div>
												<strong>Setup Organization</strong>
												<p>Configure your business & GST info</p>
											</div>
										</div>
										<div className="step-box">
											<span>2</span>
											<div>
												<strong>Setup Inventory</strong>
												<p>Add products and manage your stock</p>
											</div>
										</div>
										<div className="step-box">
											<span>3</span>
											<div>
												<strong>Start Creating Invoices</strong>
												<p>Create your first invoice and send to customers</p>
											</div>
										</div>
									</div>
								)}

								<div className="d-flex justify-content-center align-items-center">
									{isOnboardingCompleted ? (
										<Button
											className="btn btn-primary"
											onClick={() => navigate(`/user/${subDomain}/settings/my-subscription`)}
											button="Go to Subscription"
										/>
									) : (
										<Button
											className="btn btn-primary mt-4"
											onClick={() => navigate(`/organizationDetail`)}
											button="Complete Organisation Setup"
										/>
									)}
								</div>
							</div>

							{/* RIGHT — Order Summary */}
							<div className="order-summary-payment">
								<h5>Order Summary</h5>

								<div className="summary-row">
									<span>Subscription ID</span>
									<span style={{ fontSize: 11 }}>{subscription_id ?? '-'}</span>
								</div>

								<div className="summary-row">
									<span>Payment ID</span>
									<span style={{ fontSize: 11 }}>{payment_id ?? '-'}</span>
								</div>

								{invoice_id && (
									<div className="summary-row">
										<span>Invoice ID</span>
										<span style={{ fontSize: 11 }}>{invoice_id}</span>
									</div>
								)}

								{invoice_details && (
									<div className="summary-row">
										<span>Invoice No</span>
										<span>{invoice_details.invoice_number ?? '-'}</span>
									</div>
								)}

								<div className="summary-row">
									<span>Billing Period</span>
									<span style={{ textTransform: 'capitalize' }}>{billing_period}</span>
								</div>

								<hr className="border-2 border-dark opacity-50" />

								<div className="summary-row">
									<span>Sub Total</span>
									<span>{formatMoney(sub_total)}</span>
								</div>

								<div className="summary-row discount">
									<span>Discount</span>
									<span>-₹0.00</span>
								</div>

								<div className="summary-row">
									<span>GST ({tax_percentage}%)</span>
									<span>{formatMoney(tax_amount)}</span>
								</div>

								<hr className="border-2 border-dark opacity-50" />

								<div className="summary-row total">
									<span>Total Paid</span>
									<span>{formatMoney(total)}</span>
								</div>

								<p className="paid-with">Paid via {paymentMethodLabel()}</p>

								{/* Download Invoice */}
								{invoice_pdf_url ? (
									<a
										href={invoice_pdf_url}
										target="_blank"
										rel="noreferrer"
										className="btn-download"
									>
										Download Invoice
									</a>
								) : (
									<button
										className="btn-download"
										disabled
										title="Invoice will be available once payment is confirmed"
									>
										Invoice Pending
									</button>
								)}
							</div>
						</div>
					</div>
				</div>
			</main>
		</>
	);
};

export default PaymentSuccessPage;
